import i18next from 'i18next';
import { ContractDocSource, ContractStatus, IContractDoc, IPlacementContractVersion } from './types';
import { convertToFormat } from 'app/helpers/dateHelper';
import { missingField } from 'app/constants';

const rtfLooksLikeHeader = (s: string) => /^\{\\rtf/i.test(s.trim().replace(/^\uFEFF/, ''));

export function normalizeContractDocSource(raw: string | null | undefined): ContractDocSource {
  const s = raw != null ? String(raw).trim() : '';
  if (s === ContractDocSource.amieClassic || s.toLowerCase() === 'amie classic') {
    return ContractDocSource.amieClassic;
  }
  return ContractDocSource.amieWeb;
}

type TemplateWithVersions = IContractDoc & { Versions?: unknown[] };

function mapPlacementContractVersions(template: TemplateWithVersions): IPlacementContractVersion[] | undefined {
  const source = (Array.isArray(template.versions) ? template.versions : template.Versions) as unknown[] | undefined;
  if (!source?.length) return undefined;
  return source.map((item): IPlacementContractVersion => {
    const row = item as Record<string, unknown>;
    const templateHtml = row.templateHtml ?? row.TemplateHtml;
    const templateHtmlOld = row.templateHtmlOld ?? row.TemplateHtmlOld;
    const templateText = row.templateText ?? row.TemplateText;
    return {
      id: Number(row.id ?? row.Id ?? 0),
      enteredByUserId: Number(row.enteredByUserId ?? row.EnteredByUserId ?? 0),
      updatedByName: String(row.updatedByName ?? row.UpdatedByName ?? ''),
      dateEntered: String(row.dateEntered ?? row.DateEntered ?? ''),
      templateText: templateText != null ? String(templateText) : '',
      ...(templateHtml != null && String(templateHtml).trim() !== ''
        ? { templateHtml: String(templateHtml) }
        : {}),
      ...(templateHtmlOld != null && String(templateHtmlOld).trim() !== ''
        ? { templateHtmlOld: String(templateHtmlOld) }
        : {}),
    };
  });
}

export const formatTemplates = (templates: IContractDoc[], placementId: number): IContractDoc[] => {
  return templates.map((template: IContractDoc) => {
    const templateHtml =
      template.templateHtml ?? (template as IContractDoc & { TemplateHtml?: string }).TemplateHtml;
    const apiTemplateText = template.templateText;
    const apiTextRaw = apiTemplateText != null ? String(apiTemplateText) : '';
    const hasEditorHtml = templateHtml != null && String(templateHtml).trim() !== '';
    const sourceNorm = normalizeContractDocSource(template.source as string | undefined);

    /** Do not put API RTF into TinyMCE when Pending/Request has no HTML — UI shows regenerate placeholder; keep RTF for merge/save repair. */
    const omitRtfFromEditorBecausePendingHtmlMissing =
      !hasEditorHtml &&
      (template.status === ContractStatus.pending || template.status === ContractStatus.requestContract);

    const templateText = omitRtfFromEditorBecausePendingHtmlMissing
      ? ''
      : hasEditorHtml
        ? String(templateHtml)
        : apiTextRaw;

    let preservedApiTemplateTextRtf: string | undefined;
    if (omitRtfFromEditorBecausePendingHtmlMissing) {
      preservedApiTemplateTextRtf =
        apiTextRaw !== '' && rtfLooksLikeHeader(apiTextRaw)
          ? apiTextRaw
          : template.preservedApiTemplateTextRtf != null && template.preservedApiTemplateTextRtf !== ''
            ? template.preservedApiTemplateTextRtf
            : undefined;
    } else {
      const computedPreserved =
        hasEditorHtml &&
        apiTextRaw !== '' &&
        rtfLooksLikeHeader(apiTextRaw) &&
        apiTextRaw.includes('<<IMG:')
          ? apiTextRaw
          : undefined;
      preservedApiTemplateTextRtf =
        computedPreserved ??
        (template.preservedApiTemplateTextRtf != null && template.preservedApiTemplateTextRtf !== ''
          ? template.preservedApiTemplateTextRtf
          : undefined);
    }

    const versions = mapPlacementContractVersions(template as TemplateWithVersions);
    const raw = template as IContractDoc & { ShowInTsc?: boolean };
    const showInTsc = raw.showInTsc ?? raw.ShowInTsc ?? false;

    return {
      ...template,
      source: sourceNorm,
      showInTsc: Boolean(showInTsc),
      templateHtml,
      templateText,
      ...(versions ? { versions } : {}),
      ...(preservedApiTemplateTextRtf != null && preservedApiTemplateTextRtf !== ''
        ? { preservedApiTemplateTextRtf }
        : {}),
      templateName: i18next.t('templateEditor.placement.contractTemplateName', {
        version: template.version,
        placementId,
      }),
      view: {
        draftGeneratedDate: convertToFormat(template.draftCreatedOn, 'MM/DD/YYYY h:mm A', true),
        docCreatedDate: convertToFormat(template.docCreatedOn, 'MM/DD/YYYY h:mm A', true),
        updateDate: convertToFormat(template.lastUpdatedAt ?? template.updatedOn, 'MM/DD/YYYY h:mm A', true),
        publishedDate: convertToFormat(template.publishedOn, 'MM/DD/YYYY h:mm A', true),
        voidedDate: convertToFormat(template.voidedOn, 'MM/DD/YYYY h:mm A', true),
        signedDate: convertToFormat(template.signedOn, 'MM/DD/YYYY h:mm A', true),
        cardUpdateDateString: `${template.lastUpdatedByName ?? missingField} (${convertToFormat(
          template.lastUpdatedAt,
          'MM/DD/YYYY h:mm A',
          true,
        )})`,
      },
      isDraft:
        template.status === ContractStatus.draft ||
        (sourceNorm === ContractDocSource.amieWeb && template.status === ContractStatus.pending),
    };
  });
};

/**
 * Match a sidebar row after draft/add + contracts refresh.
 * Same rule as draft: API `id` / `documentId` (camel or PascalCase) ↔ list row `id` / `documentId` only.
 */
export function findContractDocMatchingDraftResponse(
  templates: IContractDoc[] | undefined,
  draftData: Record<string, unknown> | null | undefined,
): IContractDoc | undefined {
  if (!templates?.length || !draftData) return undefined;
  const matchDocId = (left: string | null | undefined, right: number | string | null | undefined) =>
    left != null && right != null && String(left) === String(right);

  const rowId = draftData.id ?? draftData.Id;
  const documentId = draftData.documentId ?? draftData.DocumentId;

  if (rowId != null && String(rowId).trim() !== '') {
    const byId = templates.find(x => matchDocId(x.id, rowId as string | number));
    if (byId) return byId;
  }
  if (documentId != null && String(documentId).trim() !== '') {
    return templates.find(x => matchDocId(x.documentId, documentId as string | number));
  }
  return undefined;
}

/** POST `placement/candidate-contract/add` body (camelCase or PascalCase from API). */
export type PlacementCandidateContractAddResponse = Record<string, unknown>;

/**
 * Snackbar for add contract: prefer API `message`/`Message`; severity reflects
 * `secondStatusUpdateReturnCode` / `SecondStatusUpdateReturnCode` (non-zero ⇒ error).
 */
/**
 * 4xx/5xx body shape: `{ errorId, errorMessage }` (camel or PascalCase).
 * Returns `errorMessage` when both id and message are present; otherwise `null` (use generic i18n).
 */
export function tryGetPlacementCandidateContractApiErrorEnvelopeMessage(data: unknown): string | null {
  if (data == null || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const errorId = d.errorId ?? d.ErrorId;
  const rawMsg = d.errorMessage ?? d.ErrorMessage;
  const msg = typeof rawMsg === 'string' ? rawMsg.trim() : '';
  if (msg === '') return null;
  if (errorId === undefined || errorId === null) return null;
  return msg;
}

export function getPlacementCandidateContractAddSnack(
  data: PlacementCandidateContractAddResponse | null | undefined,
): { message: string; severity: 'success' | 'error' } {
  const d = data ?? {};
  const code = d.secondStatusUpdateReturnCode ?? d.SecondStatusUpdateReturnCode;
  const apiMsg = String(d.message ?? d.Message ?? '').trim();
  const secondStepFailed = code != null && Number(code) !== 0;

  if (apiMsg) {
    return { message: apiMsg, severity: secondStepFailed ? 'error' : 'success' };
  }
  return {
    message: i18next.t(
      secondStepFailed
        ? 'templateEditor.placement.snackbar.contractEditor.createFailure'
        : 'templateEditor.placement.snackbar.contractEditor.createSuccess',
    ),
    severity: secondStepFailed ? 'error' : 'success',
  };
}

export function mergeContractDocWithDraftResponse(
  doc: IContractDoc,
  draft: { templateHtml?: string; templateText?: string },
): IContractDoc {
  const html = draft.templateHtml ?? (draft as { TemplateHtml?: string }).TemplateHtml;
  const text = draft.templateText ?? (draft as { TemplateText?: string }).TemplateText;
  const content = html != null && html !== '' ? html : text;
  const draftTextRaw = text != null ? String(text) : '';
  const draftPreservesImgRtf =
    draftTextRaw !== '' && rtfLooksLikeHeader(draftTextRaw) && draftTextRaw.includes('<<IMG:');
  const preservedApiTemplateTextRtf = draftPreservesImgRtf ? draftTextRaw : doc.preservedApiTemplateTextRtf;
  const merged =
    content != null && content !== ''
      ? {
          ...doc,
          templateText: content,
          ...(html != null && html !== '' ? { templateHtml: html } : {}),
        }
      : { ...doc };
  return {
    ...merged,
    preservedApiTemplateTextRtf,
  };
}
